import React from 'react';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { AlertTriangle, Briefcase, MapPin, Award} from 'lucide-react';
import { VentilationGroup } from './eligibilityHelpers';

export default function EligibilityMarketSection(ctx) {
  const {
    AUTO_TYPES, accidentsCoutTotal, accidentsList, accidentsTravail, ancienneteAnnees, collapsed,
    config, effectif, experienceAnnees, loading, prefilledForMembreRef, pvReception,
    pvReceptionFiles, qualifications, referencesTravauxFiles, setAccidentsList, setAccidentsTravail, setAncienneteAnnees,
    setCollapsed, setConfig, setEffectif, setExperienceAnnees, setLoading, setPvReception,
    setPvReceptionFiles, setQualifications, setReferencesTravauxFiles, setSinistralitDate, setSinistralitFiles, setSinistresAnterieurs,
    setTerritoiresCA, setTypeMarche, setVentClientele, setVentContrat, setVentTravaux, sinistralitDate,
    sinistralitFiles, sinistresAnterieurs, territoireCATotal, territoiresCA, toggleQualif, typeMarche,
    uploadJustificatif, ventClientele, ventContrat, ventTravaux, membre, isSuperAdmin,
    nombreActivites} = ctx;

  return (
    <>
          {/* Type de marché */}
          <div>
            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
              <Label className="text-xs font-semibold text-slate-700">Type de marché principal</Label>
              {nombreActivites > 0 && AUTO_TYPES.includes(typeMarche) && (
                <Badge className="text-[10px] bg-blue-600 text-white border border-blue-700 font-semibold shadow-sm hover:bg-blue-700" data-testid="type-marche-auto-info">
                  ⚡ Auto-détecté ({nombreActivites} activité{nombreActivites > 1 ? 's' : ''} cochée{nombreActivites > 1 ? 's' : ''})
                </Badge>
              )}
            </div>
            <RadioGroup value={typeMarche} onValueChange={setTypeMarche} className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {config.types_marche.map((tm) => {
                const isBlocking = tm.blocking;
                const hasBonus = tm.score_bonus > 0;
                const isAutoType = AUTO_TYPES.includes(tm.key);
                const isAutoSelected = isAutoType && typeMarche === tm.key && nombreActivites > 0;
                // V36.63 — Pour les COOPER / EXPERT (non Super Admin), on grise
                // les types bloquants (Promoteur immobilier, Constructeur de
                // maisons individuelles) au lieu d'afficher un badge rouge.
                // Cliquer dessus est désactivé.
                const isDisabledForRole = isBlocking && !isSuperAdmin;
                return (
                  <label
                    key={tm.key}
                    className={`flex items-start gap-2 p-2.5 rounded-lg border-2 transition-all text-sm ${
                      isDisabledForRole
                        ? 'border-slate-200 bg-slate-100 opacity-60 cursor-not-allowed grayscale'
                        : typeMarche === tm.key
                          ? (isBlocking ? 'border-red-400 bg-red-50 cursor-pointer' : hasBonus ? 'border-amber-400 bg-amber-50 cursor-pointer' : 'border-indigo-400 bg-indigo-50 cursor-pointer')
                          : 'border-slate-200 hover:border-slate-300 bg-white cursor-pointer'
                    }`}
                    data-testid={`type-marche-${tm.key}`}
                    aria-disabled={isDisabledForRole}
                  >
                    <RadioGroupItem value={tm.key} className="mt-0.5" disabled={isDisabledForRole} />
                    <div className="flex-1">
                      <div className={`font-medium flex items-center gap-1.5 flex-wrap ${isDisabledForRole ? 'text-slate-500' : ''}`}>
                        {tm.label}
                        {isAutoSelected && (
                          <span className="inline-flex items-center gap-0.5 text-[9px] uppercase tracking-wide font-bold text-white bg-blue-600 border border-blue-700 rounded px-1 py-0.5 shadow-sm">
                            ⚡ Auto
                          </span>
                        )}
                      </div>
                      {/* V36.63 — Badge "Non éligible" visible UNIQUEMENT pour
                          le Super Admin (qui peut forcer la validation V36.62).
                          Pour les COOPER/EXPERT, la case est simplement grisée. */}
                      {isBlocking && isSuperAdmin && (
                        <Badge className="mt-0.5 text-[10px] bg-red-600 text-white border border-red-800 font-semibold shadow-sm hover:bg-red-700">
                          <AlertTriangle className="h-2.5 w-2.5 mr-1" /> Non éligible
                        </Badge>
                      )}
                      {/* V36.63 — Badge "+X pts validation" visible UNIQUEMENT
                          pour le Super Admin. Les Coopérateurs / Experts ne
                          voient plus ce détail technique de scoring. */}
                      {hasBonus && !isBlocking && isSuperAdmin && (
                        <Badge className="mt-0.5 text-[10px] bg-amber-500 text-white border border-amber-700 font-semibold shadow-sm hover:bg-amber-600">
                          +{tm.score_bonus} pts validation
                        </Badge>
                      )}
                    </div>
                  </label>
                );
              })}
            </RadioGroup>

            {/* V36.64 — Encart pédagogique pour les non Super Admin :
                remplace l'impression de "boîte noire" causée par le masquage
                des badges techniques (+X pts validation / Non éligible). */}
            {!isSuperAdmin && (
              <details
                className="mt-3 group rounded-md border border-slate-200 bg-gradient-to-r from-slate-50 to-white"
                data-testid="why-this-calc"
              >
                <summary className="flex items-center gap-2 cursor-pointer select-none px-2.5 py-1.5 text-[11px] text-slate-600 hover:text-slate-800 hover:bg-slate-50 rounded-md">
                  <span className="inline-flex w-4 h-4 items-center justify-center rounded-full bg-indigo-100 text-indigo-600 text-[10px] font-bold flex-shrink-0">
                    ?
                  </span>
                  <span className="font-medium">Pourquoi ce calcul&nbsp;?</span>
                  <span className="ml-auto text-[10px] text-slate-400 group-open:rotate-180 transition-transform">▾</span>
                </summary>
                <div className="px-3 pb-2.5 pt-1 text-[11px] text-slate-600 leading-relaxed">
                  Notre algorithme tient compte de votre <strong>expérience</strong>,
                  de vos <strong>activités</strong> et de la <strong>conjoncture</strong> du
                  marché pour vous proposer un <strong>tarif solidaire</strong>.
                  Certaines activités ne sont pas couvertes par cette offre, mais
                  d'autres solutions existent&nbsp;—{' '}
                  <a
                    href="mailto:contact@objectifscopoutremer.com?subject=Demande%20de%20couverture%20sp%C3%A9cifique"
                    className="text-indigo-600 hover:text-indigo-700 underline underline-offset-2 font-medium"
                  >
                    contactez-nous
                  </a>{' '}
                  pour étudier votre situation.
                </div>
              </details>
            )}
          </div>

          {/* Qualifications */}
          <div>
            <Label className="text-xs font-semibold text-slate-700 flex items-center gap-1.5 mb-2">
              <Award className="h-3 w-3" /> Qualifications professionnelles
              <span className="text-slate-400 font-normal">(plusieurs choix possibles)</span>
            </Label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {config.qualifications.map((q) => (
                <label
                  key={q.key}
                  className={`flex items-center gap-2 p-2 rounded-md border cursor-pointer text-sm transition-all ${
                    qualifications.includes(q.key) ? 'border-emerald-400 bg-emerald-50' : 'border-slate-200 bg-white hover:border-slate-300'
                  }`}
                  data-testid={`qualif-${q.key}`}
                >
                  <Checkbox
                    checked={qualifications.includes(q.key)}
                    onCheckedChange={() => toggleQualif(q.key)}
                  />
                  <span>{q.label}</span>
                </label>
              ))}
            </div>
            {/* Fallback "titre professionnel" : si aucune qualification cochée
                mais le membre a un champ profession, on l'affiche comme repère. */}
            {qualifications.length === 0 && membre?.profession && (
              <div
                className="mt-2 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-800"
                data-testid="titre-professionnel-fallback"
              >
                <Briefcase className="h-3.5 w-3.5 mt-0.5 shrink-0 text-amber-600" />
                <div>
                  <span className="font-semibold">Titre professionnel : </span>
                  <span className="italic">{membre.profession}</span>
                  <span className="block text-amber-700/80 mt-0.5">
                    Aucune qualification certifiée. Le titre professionnel est utilisé par défaut.
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Territoires CA */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
                <MapPin className="h-3 w-3" /> Chiffre d&apos;affaires par territoire (€ HT)
              </Label>
              <Badge className="text-[10px] font-bold bg-slate-900 text-white border-0 shadow-sm" data-testid="batico-territoire-total">
                Total : {territoireCATotal.toLocaleString('fr-FR')} €
              </Badge>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {config.territoires.map((t) => (
                <div key={t.key}>
                  <Label className="text-[11px] text-slate-600">{t.label}</Label>
                  <div className="relative">
                    <Input
                      type="number" min="0"
                      value={territoiresCA[t.key] ?? ''}
                      onChange={(e) => setTerritoiresCA({ ...territoiresCA, [t.key]: e.target.value })}
                      placeholder="0"
                      className="h-8 pr-7 text-sm"
                      data-testid={`territoire-${t.key}`}
                    />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 text-xs">€</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* 3 Ventilations */}
          <div className="border-t border-slate-200 pt-4 space-y-4">
            <VentilationGroup
              title="Ventilation CA par secteur de clientèle"
              items={config.ventilation_clientele}
              values={ventClientele}
              onChange={(k, v) => setVentClientele({ ...ventClientele, [k]: v })}
              testidPrefix="vent-clientele"
            />
            <VentilationGroup
              title="Ventilation CA par nature de contrat"
              items={config.ventilation_contrat}
              values={ventContrat}
              onChange={(k, v) => setVentContrat({ ...ventContrat, [k]: v })}
              testidPrefix="vent-contrat"
            />
            <VentilationGroup
              title="Ventilation CA par nature de travaux"
              items={config.ventilation_travaux}
              values={ventTravaux}
              onChange={(k, v) => setVentTravaux({ ...ventTravaux, [k]: v })}
              testidPrefix="vent-travaux"
            />
          </div>
    </>
  );
}
